<template>
  <transition name="overlay-fade">
    <div v-if="modelValue" class="picker-overlay" @click.self="close">
      <div class="picker-modal" role="dialog" aria-modal="true" aria-label="Technologies">
        <form class="picker-create" @submit.prevent="submitCreate">
          <input
              v-model="newTechnologyName"
              type="text"
              placeholder="Nouvelle technologie"
          >
          <button class="accent-btn" type="submit" :disabled="creating" title="Créer une nouvelle technologie et la sélectionner">
            {{ creating ? '...' : '+' }}
          </button>
        </form>

        <div class="picker-grid">
          <div
              v-for="technology in technologies"
              :key="technology.id"
              class="picker-card"
              :class="{ active: localSelectedIds.includes(technology.id) }"
          >
            <button
                type="button"
                class="picker-chip"
                :title="localSelectedIds.includes(technology.id) ? `Retirer ${technology.name} de la sélection` : `Ajouter ${technology.name} à la sélection`"
                @click="toggleTechnology(technology.id)"
            >
              {{ technology.name }}
            </button>
          </div>
        </div>

        <p v-if="errorText" class="error-text">{{ errorText }}</p>

        <div class="picker-actions">
          <button class="ghost-btn" type="button" title="Fermer le sélecteur de technologies" @click="close">Fermer</button>
          <button class="accent-btn" type="button" title="Valider la sélection de technologies" @click="applySelection">Valider</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'TechnologyPickerModal',

  props: {
    creating: {
      type: Boolean,
      default: false
    },
    createTechnology: {
      type: Function,
      default: null
    },
    modelValue: {
      type: Boolean,
      default: false
    },
    selectedIds: {
      type: Array,
      default: () => []
    },
    technologies: {
      type: Array,
      default: () => []
    }
  },

  emits: ['apply', 'update:modelValue'],

  data() {
    return {
      errorText: '',
      localSelectedIds: [],
      newTechnologyName: ''
    };
  },

  watch: {
    modelValue(value) {
      if (value) {
        this.localSelectedIds = [...this.selectedIds];
        this.errorText = '';
        this.newTechnologyName = '';
      }
    }
  },

  methods: {
    close() {
      this.$emit('update:modelValue', false);
    },

    toggleTechnology(technologyId) {
      const index = this.localSelectedIds.indexOf(technologyId);

      if (index >= 0) {
        this.localSelectedIds.splice(index, 1);
        return;
      }

      this.localSelectedIds.push(technologyId);
    },

    applySelection() {
      this.$emit('apply', [...this.localSelectedIds]);
      this.close();
    },

    async submitCreate() {
      const name = this.newTechnologyName.trim();
      if (!name || !this.createTechnology) {
        return;
      }

      this.errorText = '';

      try {
        const createdTechnology = await this.createTechnology(name);
        this.newTechnologyName = '';

        if (createdTechnology?.id && !this.localSelectedIds.includes(createdTechnology.id)) {
          this.localSelectedIds.push(createdTechnology.id);
        }
      } catch (error) {
        this.errorText = error?.response?.data?.error || error?.response?.data?.detail || 'Création impossible';
      }
    }
  }
}
</script>

<style scoped>
.picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 28px 16px;
  box-sizing: border-box;
}

.picker-modal {
  width: min(760px, 100%);
  max-height: min(640px, calc(100dvh - 56px));
  overflow: auto;
  background: #2a2b2e;
  border: 1px solid #444851;
  border-radius: 20px;
  box-shadow: 0 0 24px #00f0ff33;
  padding: 1rem;
}

.picker-create {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.picker-create input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #50545d;
  border-radius: 14px;
  background: #252629;
  color: white;
  padding: 0.85rem 1rem;
}

.picker-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.picker-card {
  position: relative;
  display: inline-flex;
  transition: transform 0.18s ease, filter 0.18s ease;
}

.picker-chip,
.accent-btn,
.ghost-btn {
  border-radius: 999px;
  cursor: pointer;
  padding: 0.7rem 1rem;
  border: 1px solid transparent;
  transition: transform 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.picker-chip,
.ghost-btn {
  background: #252629;
  color: white;
  border-color: #50545d;
}

.picker-card.active .picker-chip,
.accent-btn {
  background: rgba(0, 240, 255, 0.12);
  color: #00f0ff;
  border-color: #00f0ff;
}

.picker-card:hover {
  transform: translateY(-2px);
}

.picker-card:hover .picker-chip {
  border-color: #00f0ff;
  color: #00f0ff;
  box-shadow: 0 0 14px rgba(0, 240, 255, 0.18);
}

.picker-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.error-text {
  color: #ff8a8a;
  margin: 1rem 0 0;
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.22s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

@media (max-width: 640px) {
  .picker-create {
    grid-template-columns: 1fr;
  }

  .picker-actions {
    flex-direction: column;
  }
}
</style>
